import {
  ANNULER,
  LISTE,
  LISTE_SUPPRIMEE,
  POSER_UN_POST_IT,
  POSER_UNE_LISTE,
  POST_IT,
  POST_IT_SUPPRIME,
} from '../partage/vocabulaire';
import { ListeLibre } from './ListeLibre';
import { PostIt } from './PostIt';
import type { usePapiers } from './usePapiers';
import type { CarteDesPapiers } from './useGlisserUnPapier';
import './Papiers.css';

type PapiersDUneBulle = ReturnType<typeof usePapiers>;

/**
 * Les papiers posés sur la carte de la bulle au centre (chantier post-it-et-listes, tickets 02
 * et 04) : les post-it et les listes libres. Cette couche se pose dans le monde de la carte AVANT
 * les traits et les bulles : les papiers suivent le zoom et le glisser du fond, et passent sous
 * les bulles, qui restent visibles et cliquables. `carte` : ce qu'il faut pour les glisser
 * (ticket 03), le passage de l'écran à la carte.
 */
export function PapiersSurLaCarte({ papiers, carte }: { papiers: PapiersDUneBulle; carte: CarteDesPapiers }) {
  return (
    <div className="papiers-couche">
      {papiers.papiers.map((papier) =>
        papier.sorte === 'post_it' ? (
          <PostIt
            key={papier.id}
            papier={papier}
            carte={carte}
            aEcrire={papier.id === papiers.aEcrire}
            onEcrire={papiers.ecrire}
            onPlacer={papiers.placer}
            onChangerDeFond={papiers.changerDeFond}
            onSupprimer={papiers.supprimer}
          />
        ) : (
          <ListeLibre
            key={papier.id}
            papier={papier}
            carte={carte}
            aEcrire={papier.id === papiers.aEcrire}
            onEcrire={papiers.ecrire}
            onPlacer={papiers.placer}
            onChangerDeFond={papiers.changerDeFond}
            onSupprimer={papiers.supprimer}
            onAjouterUneLigne={papiers.ajouterUneLigne}
            onChangerUneLigne={papiers.changerUneLigne}
            onRetirerUneLigne={papiers.retirerUneLigne}
          />
        )
      )}
    </div>
  );
}

/**
 * « + Post-it » et « + Liste », en bas à droite de la carte (maquette `projet-ouvert.html`,
 * `.ajout-b`), et ce qu'il faut dire à côté : « Post-it supprimé · Annuler », « Liste supprimée ·
 * Annuler », ou un papier qui n'a pas pu s'écrire. Posé sur la surface, hors du monde : il ne
 * bouge pas avec le regard.
 */
export function AjoutDePapiers({ papiers }: { papiers: PapiersDUneBulle }) {
  return (
    <div className="papiers-commandes">
      {papiers.annulable ? (
        <p className="papiers-annonce" role="status">
          {papiers.annulable === 'liste' ? LISTE_SUPPRIMEE : POST_IT_SUPPRIME}
          <button type="button" className="papiers-annuler" onClick={papiers.annuler}>
            {ANNULER}
          </button>
        </p>
      ) : (
        papiers.annonce && (
          <p className="papiers-annonce" role="status">
            {papiers.annonce}
          </p>
        )
      )}
      {/* « + Liste » à gauche de « + Post-it », qui garde sa place dans le coin. */}
      <button
        type="button"
        className="papiers-ajout"
        data-papier-ajout="liste"
        title={POSER_UNE_LISTE}
        disabled={papiers.pose}
        onClick={papiers.poserUneListe}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        {LISTE}
      </button>
      <button
        type="button"
        className="papiers-ajout"
        data-papier-ajout="post-it"
        title={POSER_UN_POST_IT}
        disabled={papiers.pose}
        onClick={papiers.poserUnPostIt}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
        {POST_IT}
      </button>
    </div>
  );
}
